/**
 * A UI choice remembered on this device (chart range, series, axis).
 * localStorage can be missing or throw (private browsing): then the
 * choice just lasts for the session.
 */
import { useState } from 'react';

export function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: keep the in-memory choice only.
  }
}

/** One of ``allowed``, remembered under ``key``; ``fallback`` when unset or unknown. */
export function useStoredChoice<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    const raw = readStored(key);
    return allowed.find((a) => a === raw) ?? fallback;
  });
  const update = (next: T) => {
    setValue(next);
    writeStored(key, next);
  };
  return [value, update];
}
